import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import Section from '../components/ui/Section';
import SectionHeader from '../components/ui/SectionHeader';

// Process Step Icons
const DiscoverIcon = ({ className = '' }) => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <circle cx="11" cy="11" r="8"></circle>
    <path d="m21 21-4.35-4.35"></path>
  </svg>
);

const DesignIcon = ({ className = '' }) => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M12 20h9"></path>
    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
  </svg>
);

const BuildIcon = ({ className = '' }) => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path>
  </svg>
);

const EvolveIcon = ({ className = '' }) => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"></path>
    <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"></path>
    <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"></path>
    <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"></path>
  </svg>
);

const Process = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { language } = useLanguage();
  const isDark = theme === 'dark';
  const isRTL = language === 'ar';
  const steps = [
    {
      number: t('process.steps.discover.number'),
      title: t('process.steps.discover.title'),
      description: t('process.steps.discover.description'),
      icon: DiscoverIcon,
    },
    {
      number: t('process.steps.design.number'),
      title: t('process.steps.design.title'),
      description: t('process.steps.design.description'),
      icon: DesignIcon,
    },
    {
      number: t('process.steps.build.number'),
      title: t('process.steps.build.title'),
      description: t('process.steps.build.description'),
      icon: BuildIcon,
    },
    {
      number: t('process.steps.evolve.number'),
      title: t('process.steps.evolve.title'),
      description: t('process.steps.evolve.description'),
      icon: EvolveIcon,
    },
  ];

  return (
    <Section id="process" withFlyingElements={true}>
      <SectionHeader
        title={t('process.title')}
        subtitle={t('process.subtitle')}
        withDivider={true}
      />
      <div className="relative">
        {/* Timeline line - hidden on mobile, visible on desktop */}
        <div className={`hidden lg:block absolute top-12 left-0 right-0 h-0.5 bg-gradient-to-r from-afsc-gold via-afsc-gold-deep to-afsc-gold ${isDark ? 'opacity-100' : 'opacity-80'}`} />
        
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 relative">
          {steps.map((step, index) => (
            <motion.div
              key={step.number}
              initial={{ opacity: 0, y: 30, filter: 'blur(10px)' }}
              whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15, duration: 0.6 }}
              className="relative group"
            >
              {/* Timeline dot */}

              <div className={`${isDark ? 'bg-afsc-slate/80' : 'bg-white/60'} backdrop-blur-sm rounded-2xl p-6 border ${isDark ? 'border-white/10' : 'border-afsc-charcoal/10'} hover:border-afsc-gold/50 transition-all duration-300 h-full`}>
                <div className={`text-center ${isRTL ? 'lg:text-right' : 'lg:text-left'}`}>
                  <div className={`w-16 h-16 rounded-full ${isDark ? 'bg-afsc-gold/20' : 'bg-afsc-gold/15'} flex items-center justify-center mb-4 mx-auto lg:mx-0 group-hover:bg-afsc-gold/30 transition-colors`}>
                    <step.icon className="text-afsc-gold" />
                  </div>
                  <div className="text-afsc-gold text-sm font-bold mb-2">
                    {step.number}
                  </div>
                  <h3 className={`text-2xl font-heading font-bold mb-3 ${isDark ? 'text-white' : 'text-afsc-charcoal'}`}>
                    {step.title}
                  </h3>
                  <p className={`leading-relaxed ${isDark ? 'text-white/70' : 'text-afsc-charcoal/70'}`}>
                    {step.description}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </Section>
  );
};

export default Process;

